import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { submitFeedback } from "../services/feedbackService";
import appLaunchIcon from "../assets/app-launch-icon.png";
import "../styles/pages/feedback.css";

const MAX_FILES = 2;
const MAX_FILE_BYTES = 7 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "heic", "heif"];

function extensionOf(file) {
  return String(file?.name || "").split(".").pop().toLowerCase();
}

function fileAllowed(file) {
  const type = String(file?.type || "").toLowerCase();
  return ["image/jpeg", "image/png", "image/heic", "image/heif"].includes(type) ||
    ALLOWED_EXTENSIONS.includes(extensionOf(file));
}

function formatFileSize(bytes, lang) {
  const value = Number(bytes || 0) / (1024 * 1024);
  return `${new Intl.NumberFormat(lang === "en" ? "en-US" : "tr-TR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: value < 1 ? 1 : 0,
  }).format(value)} MB`;
}

function FeedbackIcon({ name, className = "" }) {
  let content = null;

  if (name === "feature") {
    content = (
      <>
        <path d="m12 3 1.25 3.75L17 8l-3.75 1.25L12 13l-1.25-3.75L7 8l3.75-1.25L12 3Z" />
        <path d="m18 14 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14Z" />
        <path d="m5 13 .8 2.2L8 16l-2.2.8L5 19l-.8-2.2L2 16l2.2-.8L5 13Z" />
      </>
    );
  } else if (name === "bug") {
    content = (
      <>
        <path d="M8.5 8.5h7v7a3.5 3.5 0 0 1-7 0v-7Z" />
        <path d="M10 8.5V7a2 2 0 0 1 4 0v1.5M5 10h3.5M15.5 10H19M5 14h3.5M15.5 14H19M6 19l2.8-2.2M18 19l-2.8-2.2M7 5l2.2 2M17 5l-2.2 2" />
      </>
    );
  } else if (name === "general") {
    content = (
      <>
        <path d="M5 5.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-7l-4.5 3v-3H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z" />
        <path d="M7.5 9.5h9M7.5 13h6" />
      </>
    );
  } else if (name === "image") {
    content = (
      <>
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <circle cx="9" cy="10" r="1.5" />
        <path d="m5.5 18 4.2-4.2 3 2.7 2.3-2.3 3.5 3.8" />
      </>
    );
  } else if (name === "shield") {
    content = (
      <>
        <path d="M12 3 5 6v5c0 4.6 2.9 8.1 7 10 4.1-1.9 7-5.4 7-10V6l-7-3Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    );
  } else if (name === "send") {
    content = (
      <>
        <path d="m21 3-7.2 18-3.1-7.7L3 10.2 21 3Z" />
        <path d="m10.7 13.3 4-4" />
      </>
    );
  } else if (name === "check") {
    content = <path d="m5 12.5 4.2 4.2L19 7" />;
  } else if (name === "close") {
    content = <path d="m7 7 10 10M17 7 7 17" />;
  } else if (name === "alert") {
    content = (
      <>
        <path d="M12 4 3.5 19h17L12 4Z" />
        <path d="M12 9v4M12 16.5h.01" />
      </>
    );
  }

  return (
    <svg
      className={`feedbackIcon ${className}`.trim()}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {content}
    </svg>
  );
}

export default function FeedbackPage({ familyId, onBack }) {
  const { t, lang } = useLanguage();
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const previews = useMemo(
    () => screenshots.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [screenshots]
  );

  useEffect(() => () => previews.forEach((item) => URL.revokeObjectURL(item.url)), [previews]);

  const trimmedLength = message.trim().length;
  const messageReady = trimmedLength >= 20 && message.length <= 1000;
  const messageProgress = Math.min(100, (message.length / 1000) * 100);
  const remainingToMinimum = Math.max(0, 20 - trimmedLength);

  const categories = [
    {
      value: "feature",
      icon: "feature",
      label: t("feedback.category.feature"),
      description: t("feedback.category.feature.description"),
    },
    {
      value: "bug",
      icon: "bug",
      label: t("feedback.category.bug"),
      description: t("feedback.category.bug.description"),
    },
    {
      value: "general",
      icon: "general",
      label: t("feedback.category.general"),
      description: t("feedback.category.general.description"),
    },
  ];

  const selectCategory = (value) => {
    setCategory(value);
    setError("");
    if (value !== "bug") {
      setScreenshots([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addScreenshots = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;

    if (screenshots.length + selected.length > MAX_FILES) {
      setError(t("feedback.error.maxFiles"));
      return;
    }
    if (selected.some((file) => !fileAllowed(file))) {
      setError(t("feedback.error.fileType"));
      return;
    }
    if (selected.some((file) => file.size <= 0 || file.size > MAX_FILE_BYTES)) {
      setError(t("feedback.error.fileSize"));
      return;
    }
    setError("");
    setScreenshots((current) => [...current, ...selected]);
  };

  const removeScreenshot = (index) => {
    setError("");
    setScreenshots((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const send = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 20) {
      setError(t("feedback.error.minLength"));
      return;
    }
    if (trimmed.length > 1000) {
      setError(t("feedback.error.maxLength"));
      return;
    }
    if (!familyId || sending) return;

    try {
      setSending(true);
      setError("");
      await submitFeedback({
        familyId,
        category,
        message: trimmed,
        screenshots: category === "bug" ? screenshots : [],
        language: lang,
      });
      setSuccess(true);
      window.setTimeout(() => onBack?.(), 1500);
    } catch (sendError) {
      const errorKey = sendError?.code === "FEEDBACK_RATE_LIMITED"
        ? "feedback.error.rateLimited"
        : ["INVALID_IMAGE_TYPE", "INVALID_IMAGE_SIGNATURE"].includes(sendError?.code)
          ? "feedback.error.invalidFile"
          : "feedback.error.send";
      setError(t(errorKey));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pageContent feedbackPage">
      <div className="feedbackAmbient" aria-hidden="true">
        <span className="feedbackAmbientOrb feedbackAmbientOrbOne" />
        <span className="feedbackAmbientOrb feedbackAmbientOrbTwo" />
      </div>

      <main className="feedbackShell">
        <section className="feedbackHero">
          <span className="feedbackHeroGlow" aria-hidden="true" />
          <div className="feedbackHeroTop">
            <div className="feedbackHeroIcon" aria-hidden="true">
              <span className="feedbackHeroIconRing" />
              <img src={appLaunchIcon} alt="" />
            </div>
            <div className="feedbackHeroCopy">
              <span className="feedbackEyebrow"><i aria-hidden="true" />{t("feedback.eyebrow")}</span>
              <h1>{t("feedback.title")}</h1>
              <p>{t("feedback.subtitle")}</p>
            </div>
          </div>
          <div className="feedbackTrustRow">
            <span><FeedbackIcon name="shield" />{t("feedback.trust.private")}</span>
            <span><FeedbackIcon name="send" />{t("feedback.trust.direct")}</span>
          </div>
        </section>

        <section className="feedbackCard">
          <div className="feedbackFormSection">
            <div className="feedbackSectionHeading">
              <span className="feedbackStep" aria-hidden="true">01</span>
              <div>
                <h2 id="feedback-category-label" className="feedbackLabel">{t("feedback.category.label")}</h2>
                <p>{t("feedback.category.hint")}</p>
              </div>
            </div>

            <div className="feedbackCategoryGrid" role="radiogroup" aria-labelledby="feedback-category-label">
              {categories.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="radio"
                  aria-checked={category === item.value}
                  data-category={item.value}
                  className={`feedbackCategory${category === item.value ? " active" : ""}`}
                  onClick={() => selectCategory(item.value)}
                >
                  <span className="feedbackCategoryIcon"><FeedbackIcon name={item.icon} /></span>
                  <span className="feedbackCategoryCopy">
                    <strong>{item.label}</strong>
                    <small>{item.description}</small>
                  </span>
                  <span className="feedbackCategoryCheck" aria-hidden="true"><FeedbackIcon name="check" /></span>
                </button>
              ))}
            </div>
          </div>

          <div className="feedbackDivider" aria-hidden="true" />

          <div className="feedbackFormSection feedbackMessageSection">
            <div className="feedbackMessageTop">
              <div className="feedbackSectionHeading">
                <span className="feedbackStep" aria-hidden="true">02</span>
                <div>
                  <label id="feedback-message-label" className="feedbackLabel" htmlFor="feedback-message">{t("feedback.message.label")}</label>
                  <p>{t("feedback.message.sectionHint")}</p>
                </div>
              </div>
              <span className={`feedbackCharacterBadge${messageReady ? " ready" : ""}${message.length > 1000 ? " over" : ""}`}>
                {message.length} / 1000
              </span>
            </div>

            <div className={`feedbackComposer${messageReady ? " ready" : ""}`}>
              <textarea
                id="feedback-message"
                className="feedbackTextarea"
                value={message}
                maxLength={1000}
                onChange={(event) => { setMessage(event.target.value); setError(""); }}
                placeholder={t("feedback.message.placeholder")}
                aria-labelledby="feedback-message-label"
                aria-describedby="feedback-message-hint feedback-message-state"
                rows={7}
              />
              <div className="feedbackComposerFooter">
                <p id="feedback-message-hint" className="feedbackHint">{t("feedback.message.hint")}</p>
                <span id="feedback-message-state" className={`feedbackMessageState${messageReady ? " ready" : ""}`}>
                  {messageReady
                    ? t("feedback.message.ready")
                    : t("feedback.message.remaining", { count: remainingToMinimum })}
                </span>
              </div>
              <div className="feedbackCharacterTrack" aria-hidden="true">
                <span style={{ width: `${messageProgress}%` }} />
              </div>
            </div>
          </div>

          {category === "bug" && (
            <>
              <div className="feedbackDivider" aria-hidden="true" />
              <div className="feedbackFormSection feedbackAttachmentSection">
                <div className="feedbackAttachmentHeader">
                  <div className="feedbackSectionHeading">
                    <span className="feedbackStep" aria-hidden="true">03</span>
                    <div>
                      <span className="feedbackLabel">{t("feedback.screenshot.label")}</span>
                      <p>{t("feedback.screenshot.sectionHint")}</p>
                    </div>
                  </div>
                  <span className="feedbackAttachmentCount">{t("feedback.screenshot.count", { count: screenshots.length })}</span>
                </div>

                <button
                  type="button"
                  className="feedbackUploadZone"
                  disabled={screenshots.length >= MAX_FILES || sending}
                  onClick={() => fileInputRef.current?.click()}
                  aria-describedby="feedback-screenshot-hint"
                >
                  <span className="feedbackUploadIcon"><FeedbackIcon name="image" /></span>
                  <span className="feedbackUploadCopy">
                    <strong>{t("feedback.screenshot.add")}</strong>
                    <small id="feedback-screenshot-hint">{t("feedback.screenshot.hint")}</small>
                  </span>
                  <span className="feedbackUploadAction" aria-hidden="true">+</span>
                </button>
                <input
                  ref={fileInputRef}
                  className="feedbackFileInput"
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
                  onChange={addScreenshots}
                />

                {!!previews.length && (
                  <div className="feedbackPreviewGrid">
                    {previews.map(({ file, url }, index) => (
                      <article className="feedbackPreview" key={`${file.name}-${file.lastModified}-${index}`}>
                        <div className="feedbackPreviewMedia">
                          <img src={url} alt={t("feedback.screenshot.preview", { index: index + 1 })} />
                        </div>
                        <div className="feedbackPreviewMeta">
                          <span>
                            <strong>{file.name}</strong>
                            <small>{formatFileSize(file.size, lang)}</small>
                          </span>
                          <button
                            type="button"
                            aria-label={t("feedback.screenshot.remove")}
                            onClick={() => removeScreenshot(index)}
                          >
                            <FeedbackIcon name="close" />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {error && (
            <div className="feedbackError" role="alert">
              <span><FeedbackIcon name="alert" /></span>
              <p>{error}</p>
            </div>
          )}

          <div className="feedbackSubmitArea">
            <div className="feedbackPrivacyNote">
              <span><FeedbackIcon name="shield" /></span>
              <div>
                <strong>{t("feedback.privacy.title")}</strong>
                <small>{t("feedback.privacy.message")}</small>
              </div>
            </div>
            <button
              type="button"
              className="feedbackSubmit"
              onClick={send}
              disabled={sending || success || !messageReady}
            >
              {sending ? <span className="feedbackSubmitSpinner" aria-hidden="true" /> : <FeedbackIcon name="send" />}
              <span>{sending ? t("feedback.sending") : t("feedback.send")}</span>
            </button>
          </div>
        </section>
      </main>

      {success && (
        <div className="feedbackSuccessOverlay" role="status" aria-live="polite">
          <div className="feedbackSuccessCard">
            <div className="feedbackSuccessVisual" aria-hidden="true">
              <span className="feedbackSuccessRing" />
              <span className="feedbackSuccessCheck"><FeedbackIcon name="check" /></span>
            </div>
            <h2>{t("feedback.success.title")}</h2>
            <p>{t("feedback.success.message")}</p>
            <span className="feedbackSuccessProgress" aria-hidden="true" />
          </div>
        </div>
      )}
    </div>
  );
}
